import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from "react";
import {
  ToastProvider as LGToastProvider,
  Variant,
  useToast,
} from "@leafygreen-ui/toast";
import { Toaster } from "@via-ds/components/toast";
import { WordBreak } from "../../components/styles";
import {
  TOAST_TIMEOUT,
  mapLeafyGreenVariantToTitle,
  mapLeafyGreenVariantToToast,
} from "./constants";
import { DispatchToast, DispatchToastWithProgress, ToastParams } from "./types";

interface ToastContextState {
  success: DispatchToast;
  warning: DispatchToast;
  error: DispatchToast;
  info: DispatchToast;
  progress: DispatchToastWithProgress;
}

export const ToastContext = createContext<ToastContextState | null>(null);

const useToastContext = (): ToastContextState => {
  const context = useContext(ToastContext);
  if (context === null || context === undefined) {
    throw new Error("useToastContext must be used within a ToastProvider");
  }
  return context;
};

const ToastProviderCore: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { pushToast } = useToast();
  const dispatchToast = useCallback(
    ({
      closable,
      message,
      onClose,
      progress,
      shouldTimeout,
      timeout,
      title,
      variant,
    }: ToastParams) => {
      let toastTimeout = null;
      if (timeout !== undefined) {
        toastTimeout = timeout;
      } else if (shouldTimeout) {
        toastTimeout = TOAST_TIMEOUT;
      }

      return pushToast({
        // @ts-expect-error: data-variant doesn't exist as a prop on Toast but it will be propagated correctly.
        "data-variant": mapLeafyGreenVariantToToast[variant],
        description: <WordBreak>{message}</WordBreak>,
        dismissible: closable,
        onClose: closable ? onClose : undefined,
        progress,
        timeout: toastTimeout,
        title: title || mapLeafyGreenVariantToTitle[variant],
        variant,
      });
    },
    [pushToast],
  );

  const toastContext: ToastContextState = useMemo(() => {
    const defaultOptions = {
      onClose: () => {},
      shouldTimeout: true,
      title: "",
    };

    return {
      error: (message = "", closable = true, options = {}) =>
        dispatchToast({
          closable,
          message,
          variant: Variant.Warning,
          ...defaultOptions,
          ...options,
        }),
      info: (message = "", closable = true, options = {}) =>
        dispatchToast({
          closable,
          message,
          variant: Variant.Note,
          ...defaultOptions,
          ...options,
        }),
      progress: (message = "", progress = 0.5, closable = true, options = {}) =>
        dispatchToast({
          closable,
          message,
          progress,
          variant: Variant.Progress,
          ...defaultOptions,
          ...options,
        }),
      success: (message = "", closable = true, options = {}) =>
        dispatchToast({
          closable,
          message,
          variant: Variant.Success,
          ...defaultOptions,
          ...options,
        }),
      warning: (message = "", closable = true, options = {}) =>
        dispatchToast({
          closable,
          message,
          variant: Variant.Important,
          ...defaultOptions,
          ...options,
        }),
    };
  }, [dispatchToast]);

  return (
    <ToastContext.Provider value={toastContext}>
      {children}
    </ToastContext.Provider>
  );
};

const ToastProvider: React.FC<{
  children?: ReactNode;
  portalClassName?: string;
}> = ({ children, portalClassName }) => (
  <LGToastProvider portalClassName={portalClassName}>
    <ToastProviderCore>
      {children}
      <Toaster />
    </ToastProviderCore>
  </LGToastProvider>
);

export { ToastProvider, useToastContext };
